import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { LinkButton } from 'components/common';
import {
  CalendarEventTilePicture,
  CalendarEventTileHeader,
  CalendarEventDateString
} from '.';

import type { CalendarEvent } from 'shared-types';

type CalendarEventAttributes = Pick<
  CalendarEvent,
  'id' | 'title' | 'description' | 'endDate' | 'imageUrl' | 'startDate'
>;

export type CalendarEventInTile = CalendarEventAttributes & {
  userIsEnrolled: boolean;
};

interface Props {
  event: CalendarEventInTile;
  eventType: 'available' | 'user';
}

const CalendarEventTile = ({ event }: Props) => {
  const { messages, theme } = useReduxState();
  const router = useRouter();

  const handleClick = () => {
    router.push(`/calendar/${event.id}`);
  };
  if (!event) return null;

  return (
    <div className="event-tile" onClick={handleClick}>
      <div className="picture">
        <CalendarEventTilePicture picture={event.imageUrl} size={480} />
      </div>

      <div className="content">
        <CalendarEventTileHeader event={event} />

        <div className="event-dates">
          <CalendarEventDateString
            startDate={event.startDate as string}
            endDate={event.endDate as string}
          />
        </div>

        <div className="footer">
          <div className="actions">
            <LinkButton
              href={`/calendar/${event.id}`}
              size="small"
              text={messages.commonOpen}
              {...{ isRounded: true }}
            />
          </div>
        </div>
      </div>

      <style jsx>{`
        .event-tile {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          box-shadow: ${theme.cardBoxShadow};
          cursor: pointer;
          display: flex;
          flex-direction: column;
          transition: box-shadow 0.3s;
          height: 100%;
        }
        .event-tile:focus,
        .event-tile:hover {
          box-shadow: ${theme.cardBoxShadowActive};
        }
        .picture {
          padding: 15px 15px 0;
        }
        .content {
          border-radius: ${theme.cardBorderRadius};
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          min-height: 140px;
          padding: 15px;
          height: calc(100% - 95px);
        }
        .event-dates {
          color: #555;
          font-size: 1.3rem;
          font-weight: 600;
          margin-top: 5px;
        }
        .description {
          color: ${theme.textColorLight};
          display: -webkit-box;
          font-size: 1.4rem;
          margin-top: 5px;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
        .footer {
          align-items: flex-start;
          display: flex;
          flex-direction: column;
          gap: 15px;
          margin-top: auto;
          padding-top: 30px;
        }
        .progress {
          display: inline-flex;
          width: 100%;
        }
        .steps {
          border-radius: 0 0 ${theme.cardBorderRadius};
          flex-grow: 1;
          overflow-y: scroll;
        }
        @media (min-width: ${breakpoints.small}px) {
          .footer {
            align-items: flex-end;
            flex-direction: row;
          }
        }
      `}</style>
    </div>
  );
};

export default CalendarEventTile;
